import { SnakeToCamelCase } from "@literati/kids-api";
import {
  SCHOOL_GRADE_DICTIONARY,
  SchoolGradesLabel,
  isGradeLevelKey,
} from "constants/schools";
import { camelCase } from "lodash";

/**
 * Converts a plaintext string into a readable URL
 * that is acceptable to displaying for users.
 *
 * @example
 * ```
 * const result = formatPlainTextAsUrl('  This    Is An  Example   123  !')
 *
 * console.log(result)
 * // this-is-an-example-123
 * ```
 */
export const formatPlainTextAsUrl = (plainString: string) => {
  if (!plainString) return "";

  return (
    plainString
      .trim()
      /**
       * Replace accented unicode characters with their ascii equivalents
       * The normalize() function with the 'NFD' argument converts the string to
       * Unicode Normalization Form D (NFD), which separates each accented character into
       * its base character and its accent(s).
       *
       * The regular expression /[\u0300-\u036f]/g matches any character in the Unicode
       * Combining Diacritical Marks range, which includes most accents and diacritical marks.
       *
       * @see https://en.wikipedia.org/wiki/Combining_Diacritical_Marks
       */
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      // replace & with "and"
      .replace(/ & /g, " and ")
      // delete all remaining non-alphanumeric and non-hyphen/underscore/space characters
      .replace(/[^A-Za-z0-9\-_\s]/g, "")
      // replace all remaining non-alpha-numeric characters with a hyphen
      .replace(/[-_\s]+/g, "-")
      // if the result string is only '-' (which can happen if the the
      // input only contains illegal characters and spaces), return an empty string
      .replace(/^-$/g, "")
      .toLowerCase()
  );
};

export const formatGradeFromNumber = (
  n: number | string
): SchoolGradesLabel | null =>
  isGradeLevelKey(n) ? SCHOOL_GRADE_DICTIONARY[n] : null;

export const addProtocolToUrl = (url: string) =>
  url?.includes("http") ? url : `https://${url}`;

export function trimTags(input: string) {
  return input?.replace(/(<([^>]+)>)/gi, "");
}

/** Words not capitalized in title case, according to the Chicago Manual of Style */
const TITLE_CASE_EXCLUSION_WORD_SET = new Set([
  "a",
  "an",
  "and",
  "as",
  "at",
  "but",
  "by",
  "for",
  "from",
  "in",
  "nor",
  "of",
  "on",
  "or",
  "over",
  "so",
  "the",
  "to",
  // this one could need capitalization if used adverbially--i.e. "look up",
  // but it's such an edge case here that it's not worth implementing right now
  "up",
  "yet",
]);

export function titleCase(str: string): string {
  if (typeof str !== "string") {
    return str;
  }

  // Split the string into an array of words
  const words = str.toLowerCase().split(" ");

  // Capitalize the first letter of each word
  const titleCaseWords = words.map((word, index) => {
    if (index === 0 || !TITLE_CASE_EXCLUSION_WORD_SET.has(word)) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    } else {
      return word;
    }
  });

  return titleCaseWords.join(" ");
}

/** `camelCase` with stronger static typing */
export const snakeToCamelCase = <S extends string>(s: S) =>
  camelCase(s) as SnakeToCamelCase<S>;
